import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { SiFacebook, SiLinkedin, SiX } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationLinks = [
    { to: ROUTE_PATHS.HOME, label: 'Home' },
    { to: ROUTE_PATHS.ABOUT, label: 'About' },
    { to: ROUTE_PATHS.SERVICES, label: 'Services' },
    { to: ROUTE_PATHS.NEWS, label: 'News' },
    { to: ROUTE_PATHS.CONTACT, label: 'Contact' },
  ];

  const serviceAreas = [
    'Nassau/New Providence',
    'Grand Bahama',
    'Abaco',
    'Eleuthera',
    'Exuma',
    'Family Islands',
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-primary shadow-lg' : 'bg-primary/95'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-3">
              <img 
                src={IMAGES.CLH_LOGO} 
                alt="C.L. & H Services Logo" 
                className="h-16 w-16 object-contain"
              />
              <div className="hidden md:block">
                <div className="text-white font-bold text-xl">C.L. & H Services</div>
                <div className="text-white/80 text-xs">Logistics & Marine Solutions</div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center space-x-1">
              {navigationLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:block">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-white">
                <Link to={ROUTE_PATHS.QUOTE}>Request Quote</Link>
              </Button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white p-2 hover:bg-white/10 rounded-md transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-primary border-t border-white/10">
            <nav className="container mx-auto px-4 py-4 space-y-2">
              {navigationLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="pt-2">
                <Button asChild size="lg" className="w-full bg-accent hover:bg-accent/90 text-white">
                  <Link to={ROUTE_PATHS.QUOTE} onClick={() => setMobileMenuOpen(false)}>
                    Request Quote
                  </Link>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 pt-20">{children}</main>

      <footer className="bg-muted border-t border-border mt-auto">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src={IMAGES.CLH_LOGO} 
                  alt="C.L. & H Services Logo" 
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <div className="text-foreground font-bold text-lg">C.L. & H Services</div>
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-4">
                Comprehensive logistics, marine, and industrial solutions across the Bahamas.
              </p>
              <div className="flex space-x-3">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-secondary rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                  aria-label="Facebook"
                >
                  <SiFacebook size={18} />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-secondary rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                  aria-label="LinkedIn"
                >
                  <SiLinkedin size={18} />
                </a>
                <a
                  href="https://x.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-secondary rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                  aria-label="X (Twitter)"
                >
                  <SiX size={18} />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-foreground font-semibold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to={ROUTE_PATHS.ABOUT}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.SERVICES}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    Our Services
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-foreground font-semibold text-lg mb-4">Contact Info</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Phone size={18} className="text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-muted-foreground text-sm">24/7 Hotline</div>
                    <a
                      href="tel:+12427278293"
                      className="text-foreground hover:text-primary transition-colors text-sm font-medium"
                    >
                      +1 (242) 727-8293
                    </a>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <Mail size={18} className="text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-muted-foreground text-sm">Email</div>
                    <a
                      href="mailto:clhservicesltdnassau@gmail.com"
                      className="text-foreground hover:text-primary transition-colors text-sm font-medium"
                    >
                      clhservicesltdnassau@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <MapPin size={18} className="text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-muted-foreground text-sm">Office</div>
                    <p className="text-foreground text-sm font-medium">
                      569 Carmichael Road<br />Nassau, New Providence<br />The Bahamas
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center">
            <p className="text-muted-foreground text-sm">
              © 2026 C.L. & H Services Limited. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}